import log from '@/lib/logger';
import React, {useEffect, useState} from 'react';
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu';
import {Button} from '@/components/ui/button';
import {AlignJustify, Coins, PiggyBank, Plus, UserCheck, UserX} from 'lucide-react';
import {useNavigate} from 'react-router-dom';
import {Text} from "@/components/tailwind/text.tsx";
import {Member} from "@/models/member.ts";
import MemberBookPointsDialog from "@/components/member-book-points-dialog.tsx";
import SocialAccountDonationDialog from "@/components/social-account-donation-dialog.tsx";
import {useAuth} from "@/context/use-auth.tsx";
import {addPointsToMember, CurrentPointsDTO, ResignationPointsTarget} from "@/services/member-services.ts";
import {getSocialAccountEnabled} from "@/services/social-account-settings-services.ts";
import {VoluntaryPointDonationResult} from "@/services/social-account-services.ts";
import {isDefined} from "@/helpers/sanitizer.ts";
import {ConfirmationDialog, useConfirmationDialog} from "@/components/confirmation-dialog";
import {useMembersTranslation} from "@/i18n/hooks";
import MemberCancellationDialog from "@/components/member-cancellation-dialog.tsx";

interface MemberActionsProps {
    onAddPoints: (addPointsDTO: CurrentPointsDTO) => void;
    onDonatePoints: (newMemberBalance: number) => void;
    onCancelMembership: (
        cancellationDate: string,
        resignationReasonId: string,
        pointsTarget?: ResignationPointsTarget,
        pointsTargetMemberId?: string
    ) => void;
    onRevokeCancellation: () => void;
    memberData: Member
}

const MemberActions: React.FC<MemberActionsProps> = ({onAddPoints, onDonatePoints, onCancelMembership, onRevokeCancellation, memberData}) => {
    const [isPointsDialogOpen, setPointsDialogOpen] = useState(false);
    const [isDonationDialogOpen, setDonationDialogOpen] = useState(false);
    const [isCancellationDialogOpen, setCancellationDialogOpen] = useState(false);
    const [socialAccountEnabled, setSocialAccountEnabled] = useState(false);

    const {t} = useMembersTranslation()
    const {token} = useAuth()
    const navigate = useNavigate()
    const revokeConfirmationDialog = useConfirmationDialog()

    useEffect(() => {
        if (!token) return;
        getSocialAccountEnabled(token)
            .then(setSocialAccountEnabled)
            .catch(() => setSocialAccountEnabled(false));
    }, [token]);

    const hasPendingCancellation = Boolean(memberData.membershipEndDate);

    const handleRevokeClick = () => {
        revokeConfirmationDialog.openWithConfig({
            title: "memberActions.revokeCancellationConfirmation.title",
            description: "memberActions.revokeCancellationConfirmation.description",
            cancelButton: {
                text: "memberActions.revokeCancellationConfirmation.buttons.cancel.text",
                ariaLabel: "memberActions.revokeCancellationConfirmation.buttons.cancel.ariaLabel"
            },
            confirmButton: {
                text: "memberActions.revokeCancellationConfirmation.buttons.submit.text",
                ariaLabel: "memberActions.revokeCancellationConfirmation.buttons.submit.ariaLabel",
                color: "green"
            }
        });
    };

    function handleAddPoints() {
        setPointsDialogOpen(true)
    }

    function handleDonatePoints() {
        setDonationDialogOpen(true)
    }

    function onDonationCompleted(result: VoluntaryPointDonationResult) {
        onDonatePoints(result.memberBalanceAfter)
        setDonationDialogOpen(false)
    }

    function handleCancelMembership() {
        setCancellationDialogOpen(true)
    }
    
    function handleCancellationConfirm(
        cancellationDate: string,
        resignationReasonId: string,
        pointsTarget?: ResignationPointsTarget,
        pointsTargetMemberId?: string
    ) {
        onCancelMembership(cancellationDate, resignationReasonId, pointsTarget, pointsTargetMemberId);
        setCancellationDialogOpen(false);
    }

    function onPointDialogSubmission(pointsData: { points: number, description: string }) {
        const addPointsDTO = {
            memberId: memberData.id,
            points: pointsData.points,
            description: pointsData.description
        }

        const addPointsRequest = addPointsToMember(token, addPointsDTO)

        addPointsRequest
            .then((response) => {
                if (isDefined<CurrentPointsDTO>(response)) {
                    // TODO Update points in member data
                    onAddPoints(response)
                }
            })
            .catch((errorDetails) => {
                // FIXME show error message
                log.warn("Unable to add points to member. Error details: ", errorDetails)
            })
            .finally(() => {
                setPointsDialogOpen(false)
            })
    }

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="px_3" data-testid="member-actions-button">
                        <AlignJustify className="mr-2 h-4 w-4 text-gray-600 dark:text-gray-200"/>
                        <Text>{t("memberActions.actionsButton")}</Text>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={handleAddPoints} data-testid="member-add-points-action">
                        <Plus className="mr-2 h-4 w-4"/>
                        <Text>{t("memberActions.addPoints")}</Text>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={() => navigate(`/members/${memberData.id}/points-statement?from=member`)}
                        data-testid="member-points-statement-action"
                    >
                        <Coins className="mr-2 h-4 w-4"/>
                        <Text>{t("memberActions.showPointsStatement")}</Text>
                    </DropdownMenuItem>
                    {socialAccountEnabled && (
                        <DropdownMenuItem onClick={handleDonatePoints} data-testid="member-donate-points-action">
                            <PiggyBank className="mr-2 h-4 w-4"/>
                            <Text>{t("memberActions.donatePoints.action")}</Text>
                        </DropdownMenuItem>
                    )}
                    {hasPendingCancellation ? (
                        <DropdownMenuItem onClick={handleRevokeClick} data-testid="member-revoke-cancellation-action">
                            <UserCheck className="mr-2 h-4 w-4"/>
                            <Text>{t("memberActions.revokeCancellation")}</Text>
                        </DropdownMenuItem>
                    ) : (
                        <DropdownMenuItem onClick={handleCancelMembership} data-testid="member-cancel-membership-action">
                            <UserX className="mr-2 h-4 w-4"/>
                            <Text>{t("memberActions.cancelMembership")}</Text>
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <MemberBookPointsDialog
                memberData={memberData}
                isOpen={isPointsDialogOpen}
                onClose={() => setPointsDialogOpen(false)}
                onSubmission={onPointDialogSubmission}
            />

            <SocialAccountDonationDialog
                memberData={memberData}
                isOpen={isDonationDialogOpen}
                onClose={() => setDonationDialogOpen(false)}
                onDonated={onDonationCompleted}
            />

            <MemberCancellationDialog
                memberData={memberData}
                isOpen={isCancellationDialogOpen}
                onClose={() => setCancellationDialogOpen(false)}
                onConfirm={handleCancellationConfirm}
            />

            {revokeConfirmationDialog.dialogProps && (
                <ConfirmationDialog
                    {...revokeConfirmationDialog.dialogProps}
                    onConfirm={onRevokeCancellation}
                    namespace="members"
                />
            )}

        </>
    );
};

export default MemberActions;
